<template>
  <UCard v-if="!bare">
    <template #header>
      <h3 class="font-semibold text-highlighted">Filtre</h3>
    </template>

    <div class="space-y-5">
      <UFormField v-if="!hideSort" label="Sortare">
        <USelect
          v-model="localFilters.sortBy"
          :items="sortOptions"
          value-key="value"
          label-key="label"
          @update:model-value="emitFilters"
        />
      </UFormField>

      <ProductCategoryFilter />

      <ProductFiltersFields
        :local-filters="localFilters"
        :attribute-selections="attributeSelections"
        :filterable-attributes="filterableAttributes"
        :attribute-counts="attributeCounts"
        :facets-ready="!!facets"
        :hide-reset="bare"
        :brand-facets="facets?.brands"
        :on-sale-count="facets?.onSaleCount"
        :availability-facets="facets?.availability"
        :refurbished-count="facets?.refurbishedCount"
        @filters-change="emitFilters"
        @attribute-change="onAttributeChange"
        @reset="reset"
      />
    </div>
  </UCard>

  <div v-else class="space-y-5">
    <UFormField v-if="!hideSort" label="Sortare">
      <USelect
        v-model="localFilters.sortBy"
        :items="sortOptions"
        value-key="value"
        label-key="label"
        @update:model-value="emitFilters"
      />
    </UFormField>

    <ProductCategoryFilter />

    <ProductFiltersFields
      :local-filters="localFilters"
      :attribute-selections="attributeSelections"
      :filterable-attributes="filterableAttributes"
      :attribute-counts="attributeCounts"
      :facets-ready="!!facets"
      :hide-reset="bare"
      :brand-facets="facets?.brands"
      :on-sale-count="facets?.onSaleCount"
      :availability-facets="facets?.availability"
      :refurbished-count="facets?.refurbishedCount"
      @filters-change="emitFilters"
      @attribute-change="onAttributeChange"
      @reset="reset"
    />
  </div>
</template>

<script setup lang="ts">
import type { ProductFilterState } from '~/types'
import {
  PRODUCT_FILTER_SETTING_KEYS,
  sortStateFromUiValue,
  toUiSortValue,
  visibleProductSortOptions,
} from '~/utils/productFilters'

const props = withDefaults(defineProps<{
  filters: ProductFilterState
  bare?: boolean
  hideSort?: boolean
}>(), {
  bare: false,
  hideSort: false,
})

const emit = defineEmits<{
  update: [filters: ProductFilterState]
}>()

const productsStore = useProductsStore()
const { productsDefaultSort, reviewsEnabled, recommendedSortEnabled, settings } = useSiteSettings()
const brandFilterEnabled = computed(() => settings.value[PRODUCT_FILTER_SETTING_KEYS.brand] === 'true')

const localFilters = reactive<ProductFilterState>({
  ...props.filters,
  sortBy: toUiSortValue(props.filters.sortBy, props.filters.sortOrder),
})
const attributeSelections = reactive<Record<number, number[]>>({})

const sortOptions = computed(() => visibleProductSortOptions({
  reviewsEnabled: reviewsEnabled.value,
  recommendedEnabled: recommendedSortEnabled.value,
}))

const facets = computed(() => productsStore.facets)

const baseFilterableAttributes = computed(() =>
  productsStore.attributes
    .filter((attribute) => attribute.showInFilters)
    .sort((a, b) => a.sortOrder - b.sortOrder),
)

const filterableAttributes = computed(() => {
  const base = baseFilterableAttributes.value
  const f = facets.value
  if (!f) return base
  const availableIds = new Set(f.attributes.map((a) => a.id))
  return base.filter(
    (attribute) => availableIds.has(attribute.id) || (attributeSelections[attribute.id]?.length ?? 0) > 0,
  )
})

const attributeCounts = computed<Record<number, Record<number, number>>>(() => {
  const map: Record<number, Record<number, number>> = {}
  for (const attribute of facets.value?.attributes ?? []) {
    const values: Record<number, number> = {}
    for (const value of attribute.values) values[value.id] = value.count
    map[attribute.id] = values
  }
  return map
})

function syncAttributeSelections(valueIds?: number[]) {
  for (const key of Object.keys(attributeSelections)) {
    delete attributeSelections[Number(key)]
  }

  for (const attribute of baseFilterableAttributes.value) {
    const selected = (valueIds || []).filter((id) =>
      attribute.values?.some((value) => value.id === id),
    )
    attributeSelections[attribute.id] = selected.length ? selected : []
  }
}

function syncLocalFilters(val: ProductFilterState) {
  Object.assign(localFilters, {
    ...val,
    sortBy: toUiSortValue(val.sortBy, val.sortOrder),
  })
}

watch(() => props.filters, (val) => {
  syncLocalFilters(val)
  syncAttributeSelections(val.attributeValueIds)
}, { deep: true })

watch(reviewsEnabled, (enabled) => {
  if (enabled) return
  if (localFilters.sortBy !== 'rating') return
  localFilters.sortBy = productsDefaultSort.value
  emitFilters()
})

watch(recommendedSortEnabled, (enabled) => {
  if (enabled || localFilters.sortBy !== 'recommended') return
  localFilters.sortBy = productsDefaultSort.value
  emitFilters()
})

function buildFilters(): ProductFilterState {
  const attributeValueIds = Object.values(attributeSelections)
    .flat()
    .filter((id): id is number => typeof id === 'number' && id > 0)

  return {
    ...localFilters,
    ...sortStateFromUiValue(localFilters.sortBy),
    page: 1,
    brandIds: brandFilterEnabled.value && localFilters.brandIds?.length ? localFilters.brandIds : undefined,
    models: undefined,
    colors: undefined,
    storages: undefined,
    onSale: localFilters.onSale || undefined,
    refurbishedOnly: localFilters.refurbishedOnly || undefined,
    availabilityStatusIds: localFilters.availabilityStatusIds?.length
      ? localFilters.availabilityStatusIds
      : undefined,
    attributeValueIds: attributeValueIds.length ? attributeValueIds : undefined,
  }
}

function emitFilters() {
  emit('update', buildFilters())
}

function onAttributeChange() {
  emitFilters()
}

function reset() {
  Object.assign(localFilters, {
    brandIds: undefined,
    models: undefined,
    minPrice: undefined,
    maxPrice: undefined,
    colors: undefined,
    storages: undefined,
    inStock: false,
    onSale: undefined,
    refurbishedOnly: undefined,
    availabilityStatusIds: undefined,
    ...sortStateFromUiValue(productsDefaultSort.value),
    page: 1,
    attributeValueIds: undefined,
  })
  for (const key of Object.keys(attributeSelections)) {
    delete attributeSelections[Number(key)]
  }
  emitFilters()
}

onMounted(async () => {
  await Promise.all([
    productsStore.attributes.length ? Promise.resolve() : productsStore.fetchAttributes(),
    productsStore.facets ? Promise.resolve() : productsStore.fetchFacets(props.filters),
  ])
  syncAttributeSelections(props.filters.attributeValueIds)
})

defineExpose({ reset })
</script>
